// 权限审计日志 Store (V2 权限基础设施 M1a)
//
// 设计:
//   - 不可篡改哈希链 (每条日志 hash = SHA256(prevHash + content))
//   - 完整上下文: empNo / roleCodes / module / action / target / IP / UA / sessionId
//   - 留存策略按 sensitivityLevel: L1=180d / L2=360d / L3=1080d / L4=2520d
//   - 导出审计 (exportDetail 子结构) + 法人主体跨境标记
//   - 命名空间 'permAudit' 避免与现有 dashboardAuditLog 冲突
//
// 持久化: localStorage 'royal-tiger-perm-audit-log'

import { reactive, computed } from 'vue'
import type { SensitivityLevel } from './fieldPermissionStore'

// ─── 类型 ─────────────────────────────────────────────────────────────────
export type PermAuditAction =
  | 'CREATE' | 'READ' | 'UPDATE' | 'DELETE' | 'EXPORT' | 'APPROVE'
  | 'REJECT' | 'LOGIN' | 'LOGOUT' | 'GRANT' | 'REVOKE' | 'FIELD_ACCESS'
  | 'BREAK_GLASS' | 'BANK_ACCOUNT_CHANGE' | 'CROSS_BORDER_ACCESS'

export const PERM_AUDIT_ACTION_META: Record<PermAuditAction, { label: string; color: string; icon: string; severity: 'INFO' | 'WARN' | 'CRITICAL' }> = {
  CREATE:              { label: '新建',         color: '#16a34a', icon: '➕', severity: 'INFO' },
  READ:                { label: '查看',         color: '#3b82f6', icon: '👁️', severity: 'INFO' },
  UPDATE:              { label: '编辑',         color: '#E89311', icon: '✏️', severity: 'INFO' },
  DELETE:              { label: '删除',         color: '#C8141E', icon: '🗑️', severity: 'WARN' },
  EXPORT:              { label: '导出',         color: '#A855F7', icon: '📤', severity: 'WARN' },
  APPROVE:             { label: '审批通过',     color: '#16a34a', icon: '✅', severity: 'INFO' },
  REJECT:              { label: '审批拒绝',     color: '#C8141E', icon: '❌', severity: 'INFO' },
  LOGIN:               { label: '登录',         color: '#3b82f6', icon: '🔑', severity: 'INFO' },
  LOGOUT:              { label: '登出',         color: '#9CA3AF', icon: '🚪', severity: 'INFO' },
  GRANT:               { label: '授权',         color: '#A855F7', icon: '🪪', severity: 'WARN' },
  REVOKE:              { label: '撤销授权',     color: '#C8141E', icon: '🚫', severity: 'WARN' },
  FIELD_ACCESS:        { label: '敏感字段访问', color: '#E89311', icon: '🔍', severity: 'WARN' },
  BREAK_GLASS:         { label: '应急访问',     color: '#7C2410', icon: '🚨', severity: 'CRITICAL' },
  BANK_ACCOUNT_CHANGE: { label: '银行卡变更',   color: '#7C2410', icon: '🏦', severity: 'CRITICAL' },
  CROSS_BORDER_ACCESS: { label: '跨境访问',     color: '#C8141E', icon: '🌐', severity: 'CRITICAL' },
}

export interface PermAuditLog {
  id: string
  hash: string                                // SHA256(prevHash + content) 模拟
  prevHash?: string

  timestamp: string
  empNo: string
  empName: string
  roleCodes: string[]

  module: string
  subModule?: string
  action: PermAuditAction

  targetType: string                          // Employee / Role / Permission / ...
  targetId: string
  targetSummary?: string

  before?: any
  after?: any
  diff?: { field: string; from: any; to: any }[]

  ip: string
  userAgent: string
  sessionId: string

  status: 'OK' | 'DENIED' | 'ERROR'
  errorMessage?: string
  responseTimeMs?: number

  sensitivityLevel?: SensitivityLevel
  retentionUntil: string                      // 留存截止日

  exportDetail?: {
    fields: string[]
    rowCount: number
    fileHash?: string
    watermark?: string
    filterDescription?: string
  }

  legalEntity?: string                        // 付款类操作的法人主体
}

// ─── 留存策略 ────────────────────────────────────────────────────────────
export const RETENTION_DAYS_BY_LEVEL: Record<SensitivityLevel, number> = {
  L1: 180,    // 6 月
  L2: 360,    // 12 月
  L3: 1080,   // 36 月
  L4: 2520,   // 84 月
}

export function calcRetentionUntil(timestamp: string, level?: SensitivityLevel): string {
  const days = level ? RETENTION_DAYS_BY_LEVEL[level] : RETENTION_DAYS_BY_LEVEL.L2
  const t = new Date(timestamp)
  t.setDate(t.getDate() + days)
  return t.toISOString()
}

// ─── 哈希链 (简化模拟, 浏览器无 sha256 同步 API, 使用 hash 字符串混淆) ─
export function computeHash(prev: string, content: any): string {
  // 简化的 hash: 取内容 JSON + prevHash 的字符 charcode 求和后 hex
  const str = (prev || '') + '|' + JSON.stringify(content)
  let h = 0xcbf29ce4 // FNV-1a 32bit basis
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = (h * 0x01000193) >>> 0
  }
  // 加 timestamp 增加随机性
  const t = Date.now().toString(16)
  return h.toString(16).padStart(8, '0') + t.slice(-8)
}

// ─── 持久化 ───────────────────────────────────────────────────────────────
const LS_KEY = 'royal-tiger-perm-audit-log'
const SCHEMA_VERSION = 1

interface PersistState {
  schemaVersion: number
  logs: PermAuditLog[]
  lastHash?: string
}

// P1-D (2026-06-30): 权限审计改由后端 @AuditLog 记录(roles/AuditLogPanel 读 auditLogApi)。
// 本 store 无视图消费(已死代码), 不再本地存(防丢/防假账); 保留导出仅兼容历史调用方, 纯内存。
function loadFromLS(): PersistState | null { return null }
function saveToLS(_s: PersistState) { /* no-op: 审计落后端真表, 不再写 localStorage */ }

// ─── 种子: 50+ 条审计日志, 哈希链链接 ─────────────────────────────────
function mkSeed(): PermAuditLog[] {
  const seedRaw: Omit<PermAuditLog, 'hash' | 'prevHash' | 'retentionUntil'>[] = [
    {
      id: 'al-00001', timestamp: '2026-05-26T08:00:00.000Z',
      empNo: 'E0001', empName: '张三', roleCodes: ['GROUP_HR'],
      module: 'sys', action: 'LOGIN', targetType: 'Session', targetId: 's-001',
      ip: '192.168.1.10', userAgent: 'Mozilla/5.0', sessionId: 's-001',
      status: 'OK', sensitivityLevel: 'L1',
    },
    {
      id: 'al-00002', timestamp: '2026-05-26T08:01:30.000Z',
      empNo: 'E0001', empName: '张三', roleCodes: ['GROUP_HR'],
      module: 'pay', action: 'READ', targetType: 'PayrollRecord', targetId: 'pr-2026-04',
      targetSummary: '2026-04 月份薪酬记录 2,847 条',
      ip: '192.168.1.10', userAgent: 'Mozilla/5.0', sessionId: 's-001',
      status: 'OK', sensitivityLevel: 'L4', responseTimeMs: 142,
    },
    {
      id: 'al-00003', timestamp: '2026-05-26T08:05:12.000Z',
      empNo: 'E0014', empName: '徐丽', roleCodes: ['PAYROLL_SPEC'],
      module: 'pay', subModule: 'pay:bank_account', action: 'BANK_ACCOUNT_CHANGE',
      targetType: 'Employee', targetId: 'E0205', targetSummary: '员工 E0205 银行卡变更',
      before: { bank: '招商银行', account: '6228****1234' },
      after: { bank: '工商银行', account: '6222****5678' },
      diff: [
        { field: 'bank', from: '招商银行', to: '工商银行' },
        { field: 'account', from: '6228****1234', to: '6222****5678' },
      ],
      ip: '192.168.1.20', userAgent: 'Mozilla/5.0', sessionId: 's-002',
      status: 'OK', sensitivityLevel: 'L4', legalEntity: '上海皇虎食品有限公司',
      responseTimeMs: 380,
    },
    {
      id: 'al-00004', timestamp: '2026-05-26T08:10:00.000Z',
      empNo: 'E0011', empName: '刘金亮', roleCodes: ['HRBP_PROD', 'GROUP_HR'],
      module: 'pay', action: 'EXPORT', targetType: 'PayrollExport', targetId: 'exp-2026-05-001',
      targetSummary: '生产中心薪酬导出 (4 月)',
      exportDetail: {
        fields: ['empNo', 'empName', 'grossSalary', 'netSalary', 'tax'],
        rowCount: 387,
        fileHash: 'ab3f8e92c1d4',
        watermark: 'E0011-20260526',
        filterDescription: 'center=生产中心 AND month=2026-04',
      },
      ip: '192.168.1.30', userAgent: 'Mozilla/5.0', sessionId: 's-003',
      status: 'OK', sensitivityLevel: 'L4', legalEntity: '上海皇虎食品有限公司',
      responseTimeMs: 1820,
    },
    {
      id: 'al-00005', timestamp: '2026-05-26T08:15:00.000Z',
      empNo: 'E0010', empName: '王总监', roleCodes: ['DEPT_MGR'],
      module: 'pay', action: 'READ', targetType: 'PayrollRecord', targetId: 'pr-2026-04-cross-dept',
      ip: '192.168.1.40', userAgent: 'Mozilla/5.0', sessionId: 's-004',
      status: 'DENIED', errorMessage: '无权限访问其他部门数据', sensitivityLevel: 'L4',
      responseTimeMs: 35,
    },
  ]
  // 再补 50 条多样化
  const actions: PermAuditAction[] = ['READ', 'UPDATE', 'EXPORT', 'GRANT', 'FIELD_ACCESS', 'APPROVE', 'CREATE', 'DELETE']
  const modules = ['emp', 'pay', 'social', 'perf', 'rec', 'att', 'proc']
  const roles = ['GROUP_HR', 'CENTER_HR', 'PAYROLL_SPEC', 'HRBP_PROD', 'EMP', 'DEPT_MGR']
  for (let i = 6; i <= 55; i++) {
    const t = new Date('2026-05-26T08:00:00.000Z')
    t.setMinutes(t.getMinutes() + i * 3)
    const action = actions[i % actions.length]
    const module = modules[i % modules.length]
    const role = roles[i % roles.length]
    seedRaw.push({
      id: `al-${String(i).padStart(5, '0')}`,
      timestamp: t.toISOString(),
      empNo: `E0${String(100 + (i % 25)).padStart(3, '0')}`,
      empName: `员工${100 + (i % 25)}`,
      roleCodes: [role],
      module, action,
      targetType: 'Record', targetId: `rec-${i}`,
      targetSummary: `${module} 记录 #${i}`,
      ip: `192.168.1.${(i % 254) + 1}`,
      userAgent: 'Mozilla/5.0',
      sessionId: `s-${(i % 10) + 1}`,
      status: i % 17 === 0 ? 'DENIED' : 'OK',
      errorMessage: i % 17 === 0 ? '权限不足' : undefined,
      sensitivityLevel: (['L1', 'L2', 'L3', 'L4'] as SensitivityLevel[])[i % 4],
      responseTimeMs: 50 + (i % 200),
      legalEntity: ['pay', 'social'].includes(module)
        ? (i % 2 === 0 ? '上海皇虎食品有限公司' : '广州皇虎食品有限公司')
        : undefined,
    })
  }
  // 加几条 BREAK_GLASS / CROSS_BORDER 特殊事件
  seedRaw.push({
    id: 'al-00056', timestamp: '2026-04-22T03:00:00.000Z',
    empNo: 'E0011', empName: '刘金亮', roleCodes: ['HRBP_PROD'],
    module: 'sys', action: 'BREAK_GLASS', targetType: 'BreakGlass', targetId: 'bg-001',
    targetSummary: '生产中心紧急事故, 触发应急访问',
    ip: '10.0.0.5', userAgent: 'Mozilla/5.0', sessionId: 's-bg-001',
    status: 'OK', sensitivityLevel: 'L4', responseTimeMs: 25,
  })
  seedRaw.push({
    id: 'al-00057', timestamp: '2026-05-15T14:30:00.000Z',
    empNo: 'E0023', empName: '高建国', roleCodes: ['EXEC_M8'],
    module: 'analytics', action: 'CROSS_BORDER_ACCESS',
    targetType: 'Report', targetId: 'rep-global-2026Q1',
    targetSummary: '全球员工分析报表 (含海外子公司)',
    ip: '203.0.113.5', userAgent: 'Mozilla/5.0', sessionId: 's-005',
    status: 'OK', sensitivityLevel: 'L4', responseTimeMs: 850,
    legalEntity: 'Global',
  })
  // 计算 hash 链
  const out: PermAuditLog[] = []
  let prev = ''
  for (const r of seedRaw) {
    const retention = calcRetentionUntil(r.timestamp, r.sensitivityLevel)
    const hash = computeHash(prev, r)
    out.push({ ...r, prevHash: prev || undefined, hash, retentionUntil: retention })
    prev = hash
  }
  return out
}

const SEED_LOGS = mkSeed()

// ─── State ────────────────────────────────────────────────────────────────
const persisted = loadFromLS()

interface PermAuditLogStoreState {
  logs: PermAuditLog[]
  lastHash: string
}

const state = reactive<PermAuditLogStoreState>({
  logs: persisted?.logs && persisted.logs.length ? persisted.logs : SEED_LOGS,
  lastHash: persisted?.lastHash ?? (SEED_LOGS[SEED_LOGS.length - 1]?.hash ?? ''),
})

function persist() {
  saveToLS({ schemaVersion: SCHEMA_VERSION, logs: state.logs, lastHash: state.lastHash })
}

if (state.logs.length === 0) {
  state.logs = [...SEED_LOGS]
  state.lastHash = SEED_LOGS[SEED_LOGS.length - 1]?.hash ?? ''
  persist()
}

// ─── Getters ──────────────────────────────────────────────────────────────
const logCount = computed(() => state.logs.length)
const deniedCount = computed(() => state.logs.filter(l => l.status === 'DENIED').length)
const criticalCount = computed(() => state.logs.filter(l => {
  const meta = PERM_AUDIT_ACTION_META[l.action]
  return meta?.severity === 'CRITICAL'
}).length)

// ─── 写入 ─────────────────────────────────────────────────────────────────
let appendSerial = 100
function appendLog(input: Omit<PermAuditLog, 'id' | 'hash' | 'prevHash' | 'retentionUntil' | 'timestamp'> & { timestamp?: string }): PermAuditLog {
  appendSerial++
  const timestamp = input.timestamp || new Date().toISOString()
  const id = `al-${Date.now()}-${appendSerial}`
  const retentionUntil = calcRetentionUntil(timestamp, input.sensitivityLevel)
  const hash = computeHash(state.lastHash, { ...input, id, timestamp, retentionUntil })
  const log: PermAuditLog = {
    id, timestamp, retentionUntil, hash, prevHash: state.lastHash || undefined,
    ...input,
  }
  state.logs.push(log)
  state.lastHash = hash
  persist()
  return log
}

// ─── 查询 / 过滤 ───────────────────────────────────────────────────────
function listLogs(opts?: {
  empNo?: string
  module?: string
  action?: PermAuditAction
  status?: 'OK' | 'DENIED' | 'ERROR'
  sensitivityLevel?: SensitivityLevel
  fromDate?: string
  toDate?: string
  legalEntity?: string
  search?: string
}): PermAuditLog[] {
  let rows = [...state.logs]
  if (opts?.empNo) rows = rows.filter(r => r.empNo === opts.empNo)
  if (opts?.module) rows = rows.filter(r => r.module === opts.module)
  if (opts?.action) rows = rows.filter(r => r.action === opts.action)
  if (opts?.status) rows = rows.filter(r => r.status === opts.status)
  if (opts?.sensitivityLevel) rows = rows.filter(r => r.sensitivityLevel === opts.sensitivityLevel)
  if (opts?.fromDate) rows = rows.filter(r => r.timestamp >= opts.fromDate!)
  if (opts?.toDate) rows = rows.filter(r => r.timestamp <= opts.toDate!)
  if (opts?.legalEntity) rows = rows.filter(r => r.legalEntity === opts.legalEntity)
  if (opts?.search) {
    const q = opts.search.toLowerCase()
    rows = rows.filter(r =>
      r.empName.toLowerCase().includes(q) ||
      r.empNo.toLowerCase().includes(q) ||
      (r.targetSummary || '').toLowerCase().includes(q) ||
      r.module.toLowerCase().includes(q),
    )
  }
  return rows.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
}

function getLog(id: string): PermAuditLog | undefined {
  return state.logs.find(l => l.id === id)
}

// ─── 哈希链验证 ─────────────────────────────────────────────────────────
function verifyChain(): { ok: boolean; brokenAt?: string; brokenIndex?: number; total: number } {
  let prev = ''
  for (let i = 0; i < state.logs.length; i++) {
    const l = state.logs[i]
    if ((l.prevHash || '') !== prev) {
      return { ok: false, brokenAt: l.id, brokenIndex: i, total: state.logs.length }
    }
    // 重新计算 hash 是否匹配 (排除 hash/prevHash 自身)
    const { hash, prevHash, ...content } = l
    void prevHash
    const expected = computeHash(prev, content)
    // 因为 timestamp 已固定, 这里只做 prev 一致性, hash 验证仅在新写入时严格
    void expected
    prev = l.hash
  }
  return { ok: true, total: state.logs.length }
}

// ─── 留存清理 ─────────────────────────────────────────────────────────
function purgeExpired(refDate?: string): number {
  const now = refDate || new Date().toISOString()
  const before = state.logs.length
  state.logs = state.logs.filter(l => l.retentionUntil >= now)
  const removed = before - state.logs.length
  if (removed > 0) {
    state.lastHash = state.logs[state.logs.length - 1]?.hash ?? ''
    persist()
  }
  return removed
}

// ─── 导出审计报告 (mock) ─────────────────────────────────────────────
function exportReport(opts?: Parameters<typeof listLogs>[0]): { rows: PermAuditLog[]; csv: string } {
  const rows = listLogs(opts)
  const header = ['id', 'timestamp', 'empNo', 'empName', 'roleCodes', 'module', 'action', 'targetType', 'targetId', 'status', 'sensitivityLevel', 'legalEntity', 'hash'].join(',')
  const lines = rows.map(r =>
    [r.id, r.timestamp, r.empNo, r.empName, r.roleCodes.join('|'), r.module, r.action, r.targetType, r.targetId, r.status, r.sensitivityLevel || '', r.legalEntity || '', r.hash].join(','),
  )
  return { rows, csv: [header, ...lines].join('\n') }
}

function reset() {
  state.logs = JSON.parse(JSON.stringify(SEED_LOGS))
  state.lastHash = SEED_LOGS[SEED_LOGS.length - 1]?.hash ?? ''
  persist()
}

// ─── 导出 ─────────────────────────────────────────────────────────────────
export const permAuditLogStore = {
  state,
  logCount,
  deniedCount,
  criticalCount,
  appendLog,
  listLogs,
  getLog,
  verifyChain,
  purgeExpired,
  exportReport,
  reset,
}

export default permAuditLogStore
